import {
  Badge,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { mdiFilter, mdiSort } from '@mdi/js';
import Icon from '@mdi/react';
import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Loading from 'components/Loading/Loading';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getActivityActionLabel } from 'scenes/Project/components/ProjectOverview/activityFeedFormat';
import ImagePreviewDialog from 'scenes/ProjectGallery/components/imagePreviewDialog/ImagePreviewDialog';
import { IMAGE_PREVIEW_CAPABILITIES_VIEW_ONLY } from 'scenes/ProjectGallery/components/imagePreviewDialog/imagePreviewCapabilities';
import {
  selectActivityLogError,
  selectActivityLogImages,
  selectActivityLogItems,
  selectActivityLogLoading,
} from 'store/activityLog/activityLogSlice';
import { fetchActivityLogsForUserProjectDate } from 'store/activityLog/thunks/fetchActivityLogsForUserProjectDate.thunk';
import ActivityLogRow, { IMAGE_TARGET_TYPE } from './ActivityLogRow';

const DEFAULT_SORT = 'TIME_ASC';

const NO_EDIT_RIGHTS = [];

/**
 * The file permission the image preview reads, shaped from an image the API
 * says the viewer may see (as the chat shapes its attachments). No project:
 * the preview opens view-only, so nothing reads or writes the gallery's
 * data, and opening it here logs no view of the image. `known` (id -> file
 * permission) keeps each one's identity, since the preview reloads the image
 * whenever the object it is given changes.
 */
export const toPreviewFilePermission = (item, image, known) => {
  const id = item.targetId;
  const name = image.name || item.targetName || '';
  const cached = known && known.get(id);
  if (
    cached &&
    cached.name === name &&
    cached.file.mimeType === image.mimeType &&
    cached.rotation === (image.rotation || 0)
  ) {
    return cached;
  }
  const filePermission = {
    id,
    name,
    file: { mimeType: image.mimeType || '' },
    rotation: image.rotation || 0,
    createdAt: item.createdAt,
  };
  if (known) known.set(id, filePermission);
  return filePermission;
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    ...theme.component.dialogPaper,
    // Always use the full available height, even with few rows.
    height: 'calc(100% - 64px)',
    [theme.breakpoints.down('xs')]: {
      ...theme.component.dialogPaperFullscreen,
    },
  },
  dialogActions: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  // Top bar: activity count on the left, filter + sort buttons on the right.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

/**
 * Lists a user's activity logs on a project for a single day — the viewer's
 * local day, midnight to midnight (see fetchActivityLogsForUserProjectDate),
 * without the rows of opening the project. Each row shows two icons (the
 * verb, e.g. eye for "viewed", and the feature, e.g. calendar for the
 * timestamps page), the time and what the user did, worded as the dashboard
 * feed words it. Rows about an image show it small; clicking it opens the
 * gallery's image preview, view-only, paging through the day's images. A top
 * bar shows the activity count and offers sort + filter (by action) menus.
 * Visibility is controlled by the activityLog slice.
 */
const ActivityLogDialog = ({ open, onClose, userId, projectId, date }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const items = useSelector(selectActivityLogItems);
  const images = useSelector(selectActivityLogImages);
  const loading = useSelector(selectActivityLogLoading);
  const error = useSelector(selectActivityLogError);

  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [actionFilters, setActionFilters] = useState([]); // empty = all actions
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);
  // The image open in the preview (its file permission id), or null.
  const [previewId, setPreviewId] = useState(null);
  const knownPreviewsRef = useRef(new Map());

  const load = useCallback(() => {
    if (projectId && userId && date) {
      dispatch(
        fetchActivityLogsForUserProjectDate({ projectId, userId, date })
      );
    }
  }, [dispatch, projectId, userId, date]);

  useEffect(() => {
    if (open) {
      setActionFilters([]);
      setSortBy(DEFAULT_SORT);
      setPreviewId(null);
      load();
    }
  }, [open, load]);

  const sortOptions = [
    { value: 'TIME_ASC', label: t('activity_log.sort_oldest', 'Vanhin ensin') },
    {
      value: 'TIME_DESC',
      label: t('activity_log.sort_newest', 'Uusin ensin'),
    },
    {
      value: 'ACTION_ASC',
      label: t('activity_log.sort_action_asc', 'Toiminto A–Ö'),
    },
    {
      value: 'ACTION_DESC',
      label: t('activity_log.sort_action_desc', 'Toiminto Ö–A'),
    },
  ];

  const actionLabel = useCallback(
    (action) => getActivityActionLabel(t, action),
    [t]
  );

  const distinctActions = useMemo(
    () =>
      [...new Set((items || []).map((item) => item.action))].sort((a, b) =>
        actionLabel(a).localeCompare(actionLabel(b))
      ),
    [items, actionLabel]
  );

  // Aggregate (employer-project) mode returns logs from multiple projects; show the
  // project name on each row so they're distinguishable. A normal single-project
  // dialog leaves it off (redundant there).
  const showProject = useMemo(
    () => new Set((items || []).map((item) => item.projectId)).size > 1,
    [items]
  );

  const filteredItems = useMemo(() => {
    if (actionFilters.length === 0) {
      return items || [];
    }
    return (items || []).filter((item) => actionFilters.includes(item.action));
  }, [items, actionFilters]);

  const sortedItems = useMemo(() => {
    const next = [...filteredItems];
    next.sort((a, b) => {
      switch (sortBy) {
        case 'TIME_DESC':
          return (b.createdAt || '').localeCompare(a.createdAt || '');
        case 'ACTION_ASC':
          return actionLabel(a.action).localeCompare(actionLabel(b.action));
        case 'ACTION_DESC':
          return actionLabel(b.action).localeCompare(actionLabel(a.action));
        case 'TIME_ASC':
        default:
          return (a.createdAt || '').localeCompare(b.createdAt || '');
      }
    });
    return next;
  }, [filteredItems, sortBy, actionLabel]);

  // The images the preview pages through: those the viewer may see, once
  // each, in the order the list shows them.
  const previewImages = useMemo(() => {
    const seen = new Set();
    const list = [];
    sortedItems.forEach((item) => {
      const image = item.targetId && images?.[item.targetId];
      if (
        item.targetType !== IMAGE_TARGET_TYPE ||
        image?.status !== 'AVAILABLE' ||
        seen.has(item.targetId)
      ) {
        return;
      }
      seen.add(item.targetId);
      list.push(toPreviewFilePermission(item, image, knownPreviewsRef.current));
    });
    return list;
  }, [sortedItems, images]);

  const previewed = previewId
    ? previewImages.find((image) => image.id === previewId) || null
    : null;

  const handleOpenImage = useCallback((item) => {
    setPreviewId(item.targetId);
  }, []);

  const toggleActionFilter = (action) => {
    setActionFilters((prev) =>
      prev.includes(action)
        ? prev.filter((value) => value !== action)
        : [...prev, action]
    );
  };

  return (
    <>
      <Dialog
        open={open}
        classes={{ paper: classes.dialogPaper }}
        onClose={onClose}
        aria-labelledby="activity-log-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogHeader
          onClose={onClose}
          title={
            date
              ? t('activity_log.day_title', {
                  date: moment(date, 'YYYY-MM-DD').format('l'),
                  defaultValue: 'Aktiviteettiloki – {{date}}',
                })
              : t('activity_log.title', 'Aktiviteettiloki')
          }
        />
        <DialogContent>
          {loading ? (
            <Loading tiny />
          ) : error ? (
            <div role="alert">
              <Typography variant="body2" color="error" paragraph>
                {t(
                  'activity_log.load_error',
                  'Aktiviteettilokin lataus epäonnistui'
                )}
              </Typography>
              <Button onClick={load} color="dark">
                {t('common.retry', 'Yritä uudelleen')}
              </Button>
            </div>
          ) : (
            <>
              {items && items.length > 0 ? (
                <div className={classes.toolbar}>
                  <Typography variant="body2" color="textSecondary">
                    {t('activity_log.activity_count', {
                      count: sortedItems.length,
                      defaultValue: 'Tapahtumia: {{count}}',
                    })}
                  </Typography>
                  <div className={classes.toolbarActions}>
                    <Tooltip title={t('activity_log.filter', 'Suodata')}>
                      <IconButton
                        size="small"
                        onClick={(event) =>
                          setFilterAnchor(event.currentTarget)
                        }
                        aria-label={t('activity_log.filter', 'Suodata')}
                      >
                        <Badge
                          color="secondary"
                          badgeContent={actionFilters.length}
                          invisible={actionFilters.length === 0}
                        >
                          <Icon path={mdiFilter} size={1} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('activity_log.sort', 'Järjestä')}>
                      <IconButton
                        size="small"
                        onClick={(event) => setSortAnchor(event.currentTarget)}
                        aria-label={t('activity_log.sort', 'Järjestä')}
                      >
                        <Icon path={mdiSort} size={1} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ) : null}

              <Menu
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={() => setFilterAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {distinctActions.map((action) => {
                  const checked = actionFilters.includes(action);
                  return (
                    <MenuItem
                      key={action}
                      onClick={() => toggleActionFilter(action)}
                      dense
                    >
                      <ListItemIcon style={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText primary={actionLabel(action)} />
                    </MenuItem>
                  );
                })}
                {actionFilters.length > 0 && [
                  <Divider key="divider" />,
                  <MenuItem
                    key="clear"
                    onClick={() => setActionFilters([])}
                    dense
                  >
                    <ListItemText
                      primary={t(
                        'activity_log.clear_filters',
                        'Tyhjennä suodattimet'
                      )}
                    />
                  </MenuItem>,
                ]}
              </Menu>
              <Menu
                anchorEl={sortAnchor}
                open={Boolean(sortAnchor)}
                onClose={() => setSortAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {sortOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    selected={sortBy === option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setSortAnchor(null);
                    }}
                    dense
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>

              {sortedItems.length > 0 ? (
                sortedItems.map((item) => (
                  <ActivityLogRow
                    key={
                      item.sk || item.id || `${item.createdAt}-${item.action}`
                    }
                    item={item}
                    showProject={showProject}
                    image={
                      item.targetType === IMAGE_TARGET_TYPE && item.targetId
                        ? images?.[item.targetId]
                        : undefined
                    }
                    onOpenImage={handleOpenImage}
                  />
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t('activity_log.no_logs', 'Ei tapahtumia tältä päivältä')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions className={classes.dialogActions}>
          <Button onClick={onClose} color="dark">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
      {open && previewed ? (
        <ImagePreviewDialog
          open
          filePermission={previewed}
          mediaFilePermissions={previewImages}
          filePermissionIdsUserHasPermissionToEdit={NO_EDIT_RIGHTS}
          capabilities={IMAGE_PREVIEW_CAPABILITIES_VIEW_ONLY}
          onNavigate={setPreviewId}
          onClose={() => setPreviewId(null)}
        />
      ) : null}
    </>
  );
};

export default ActivityLogDialog;
